import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Users, Calendar, ArrowRight, XCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function EventInvitePage() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [inv, setInv] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const authToken = localStorage.getItem('codearena_token');
  const backTo = `/events/invite/${token}?auto=1`;

  useEffect(() => {
    axios.get(`${API}/api/event-teams/invites/preview/${token}`)
      .then((r) => setInv(r.data))
      .catch((e) => setError(e.response?.data?.detail || 'Invitation not found'));
  }, [token]);

  const act = async (kind) => {
    setBusy(true);
    try {
      const path = inv?.kind === 'claim' ? 'claims/accept' : `invites/${kind}`;
      const r = await axios.post(`${API}/api/event-teams/${path}`, { token }, { headers: { Authorization: `Bearer ${authToken}` } });
      toast.success(r.data.message);
      if (kind === 'accept') navigate(`/events/${r.data.event_id}#team-registration`);
      else setInv({ ...inv, status: 'declined' });
    } catch (e) {
      if (e.response?.status === 401) { localStorage.removeItem('codearena_token'); navigate(`/codearena/auth?redirect=${encodeURIComponent(backTo)}`); return; }
      toast.error(e.response?.data?.detail || 'Could not process invitation');
    } finally { setBusy(false); }
  };

  useEffect(() => {
    if (inv?.status === 'pending' && authToken && params.get('auto') === '1') act('accept');
  }, [inv?.status]);

  if (error) return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-[#FAFAFA] gap-3" data-testid="invite-error">
      <XCircle className="text-red-500" size={36} />
      <p className="font-semibold text-[#0A0A0A]">{error}</p>
      <Link to="/codearena/events" className="text-sm text-[#002FA7] font-medium">Browse events</Link>
    </div>
  );
  if (!inv) return <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]"><Loader2 className="animate-spin text-[#A1A1AA]" /></div>;

  const pending = inv.status === 'pending';
  return (
    <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center px-4 py-12" data-testid="event-invite-page">
      <div className="w-full max-w-lg bg-white border-2 border-[#0A0A0A] rounded-lg overflow-hidden">
        {inv.event?.cover_image && <img src={inv.event.cover_image} alt="" className="w-full h-36 object-cover" />}
        <div className="p-8">
          <p className="text-xs font-bold text-[#002FA7] uppercase tracking-[0.12em]">Team invitation</p>
          <h1 className="text-2xl font-extrabold text-[#0A0A0A] tracking-tight mt-2">{inv.kind === 'claim' ? `Your spot in “${inv.team_name}” is reserved` : `Join team “${inv.team_name}”`}</h1>
          <p className="text-sm text-[#52525B] mt-2">{inv.invited_by_name || 'A teammate'} {inv.kind === 'claim' ? 'added' : 'invited'} <b>{inv.email_masked}</b> to compete in</p>
          <div className="mt-4 p-4 rounded-lg bg-[#F4F4F5] space-y-1.5">
            <p className="font-bold text-[#0A0A0A]">{inv.event?.title}</p>
            <p className="text-xs text-[#52525B] flex items-center gap-1.5"><Users size={12} /> Hosted by {inv.event?.company_name}</p>
            {inv.event?.event_date && <p className="text-xs text-[#52525B] flex items-center gap-1.5"><Calendar size={12} /> {new Date(inv.event.event_date).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}</p>}
          </div>
          {!pending ? (
            <div className="mt-6 space-y-2" data-testid="invite-status">
              <p className="text-sm font-semibold text-[#52525B]">This {inv.kind === 'claim' ? 'link' : 'invitation'} is {inv.status}.</p>
              {inv.kind === 'claim' && <p className="text-xs text-[#52525B]">Your team spot is still reserved. Log in with <b>{inv.email_masked}</b> and use “Email me a verification code” on <Link to="/codearena/events" className="text-[#002FA7] font-semibold">My Hackathons</Link> to access it.</p>}
            </div>
          ) : !authToken ? (
            <div className="mt-6 space-y-3">
              <p className="text-sm text-[#52525B]">Log in, or create a free account with the invited email, and you'll join the team automatically.</p>
              <Link to={`/codearena/auth?redirect=${encodeURIComponent(backTo)}`} className="flex items-center justify-center gap-2 w-full bg-[#002FA7] hover:bg-[#00227A] text-white py-3 rounded font-semibold text-sm" data-testid="invite-login-btn">
                Log in / Sign up to join <ArrowRight size={16} />
              </Link>
            </div>
          ) : (
            <div className="mt-6 flex gap-3">
              <button onClick={() => act('accept')} disabled={busy} className="flex-1 bg-[#002FA7] hover:bg-[#00227A] text-white py-3 rounded font-semibold text-sm disabled:opacity-50" data-testid="invite-accept-btn">{busy ? 'Joining…' : inv.kind === 'claim' ? 'Open my team' : 'Accept & join team'}</button>
              <button onClick={() => act('decline')} disabled={busy} className="px-5 border border-[#E4E4E7] rounded text-sm font-semibold text-[#52525B]" data-testid="invite-decline-btn">Decline</button>
            </div>
          )}
          <p className="text-[11px] text-[#A1A1AA] mt-5">Expires {inv.expires_at ? new Date(inv.expires_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '—'} · single use</p>
        </div>
      </div>
    </div>
  );
}
